// Presentational pieces for the driver profile. Adapted from the prototype in
// src/pages/DriverProfilePrototype, which stays put as the design record — this
// is the production copy, so deleting that folder does not break the page.
import React, { useState } from 'react';
// Not re-exported from ui/core; imported directly, the way RecordPanel does for Collapse.
import ButtonBase from '@mui/material/ButtonBase';
import {
  Box,
  Stack,
  Typography,
  TintedStatusPill,
  Button,
  TextField,
  Divider,
  ImagePreview,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '../../ui/core';
import { hairline, softFill } from '../../ui/styles';
import { CheckCircleIcon, WarningIcon, ErrorIcon, DottedCircleIcon, ChevronDownIcon } from '../../ui/icons';
import { REMEDIES, availableActions, canReevaluateStanding, phaseStatus } from './model';

export const SectionLabel = ({ children, sx }) => (
  <Typography
    sx={{
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      ...sx,
    }}
  >
    {children}
  </Typography>
);

export const Hair = ({ sx }) => <Divider sx={{ borderColor: (t) => hairline(t), ...sx }} />;

// Re-exported so a component can pull its formatter from the same place it pulls
// everything else, while the implementations stay in model.js — pure, no React, and
// therefore testable without mounting anything.
export { timestamp, calendarDate } from './model';

export const Field = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', mb: 0.25 }}>{label}</Typography>
    {/* `anywhere`, not `break-word`: the values here are ids, emails and connected-account
        keys — single unbreakable tokens with no space to wrap at, which on a phone set
        their own column's min-content width and pushed the card past the screen. */}
    <Typography sx={{ fontSize: '13px', fontWeight: 500, overflowWrap: 'anywhere' }}>{value || '—'}</Typography>
  </Box>
);

// Keyed on the DocumentStatus values the record actually carries, plus MISSING.
// NEEDS_REVIEW reads as "needs review" rather than "pending": the difference is
// the whole reason admin gets the uncollapsed status the mobile client doesn't.
const ITEM_STATUS = {
  APPROVED: { label: 'Valid', palette: 'success', Icon: CheckCircleIcon },
  PENDING: { label: 'Pending', palette: 'warning', Icon: DottedCircleIcon },
  NEEDS_REVIEW: { label: 'Needs review', palette: 'warning', Icon: WarningIcon },
  EXPIRED: { label: 'Expired', palette: 'error', Icon: ErrorIcon },
  REJECTED: { label: 'Rejected', palette: 'error', Icon: ErrorIcon },
  MISSING: { label: 'Missing', palette: null, Icon: DottedCircleIcon },
};

const itemStatus = (key) => ITEM_STATUS[key] || ITEM_STATUS.MISSING;

export const ItemStatusChip = ({ value }) => {
  const s = itemStatus(value);
  return <TintedStatusPill palette={s.palette} label={s.label} />;
};

// `display: block` matters: MUI's SvgIcon is inline-block, so it otherwise sits
// on the text baseline and reads a couple of pixels high next to a label. The
// caller must also keep it in a center-aligned row.
export const StatusDot = ({ value, size = 17 }) => {
  const s = itemStatus(value);
  return (
    <s.Icon
      sx={{
        fontSize: size,
        display: 'block',
        color: s.palette ? `${s.palette}.main` : 'text.disabled',
        flexShrink: 0,
      }}
    />
  );
};

// One segmented control with a thumb that slides between segments rather than the
// fill jumping. Segments are equal-width so the thumb's position is just
// index × its own width — no measuring, no refs, no resize listener.
// Deep red, so white sits on it at ~6:1 rather than the ~3:1 the theme's brighter
// error red gives — a four-digit count at 11px needs the contrast.
const BADGE_RED = '#B3261E';

/**
 * How many are behind a filter, as a notification badge.
 *
 * Null and undefined render nothing rather than a zero: the counts endpoint omits a
 * state nobody is in, and "0" next to a filter reads as a loaded, empty result when
 * the truth may be that the number has not arrived yet.
 *
 * Zero itself does render — an explicit zero is a real answer, and it is the one
 * that tells a reviewer not to bother clicking.
 */
export const CountBadge = ({ count, sx }) => {
  if (count == null) return null;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '20px',
        height: '18px',
        px: '6px',
        borderRadius: '9px',
        fontSize: '11px',
        fontWeight: 700,
        lineHeight: 1,
        // Muted when empty. A red badge is a call to action, and there is nothing
        // to act on behind a zero.
        //
        // Literal rather than `error.main`: the theme's error red is tuned for text
        // and buttons, and as a filled badge it reads brighter than a status
        // indicator should on this dark surface. Both colours are fixed rather than
        // per-mode because the badge carries its own contrast either way.
        backgroundColor: (t) => (count ? BADGE_RED : softFill(t)),
        color: (t) => (count ? '#fff' : t.palette.text.disabled),
        ...sx,
      }}
    >
      {count.toLocaleString()}
    </Box>
  );
};

/**
 * A filter as a dropdown rather than a segmented row.
 *
 * The segmented control needs one column per option, so six filters spanned half
 * the header and each column was sized by the longest label. A menu costs one click
 * and gives every option room for its count — which is the point: the numbers are
 * the reason to pick one, so they have to be readable without picking it first.
 */
export const FilterMenu = ({ value, options, counts, onChange, sx }) => {
  const [anchor, setAnchor] = useState(null);
  const current = options.find((o) => o.key === value) || options[0];

  const pick = (key) => {
    setAnchor(null);
    onChange(key);
  };

  return (
    <>
      <Stack
        component="button"
        type="button"
        direction="row"
        alignItems="center"
        spacing={0.75}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        sx={{
          px: 1.25,
          py: '5px',
          borderRadius: '8px',
          backgroundColor: (t) => softFill(t),
          border: (t) => `1px solid ${hairline(t)}`,
          font: 'inherit',
          fontSize: '12px',
          fontWeight: 600,
          color: 'text.primary',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          '&:hover': { opacity: 0.8 },
          ...sx,
        }}
      >
        <span>{current.label}</span>
        <CountBadge count={counts?.[current.key]} />
        <ChevronDownIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      </Stack>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { minWidth: 232, mt: 0.5 } }}
      >
        {options.map((o) => (
          <MenuItem key={o.key} onClick={() => pick(o.key)} selected={o.key === value} sx={{ py: 0.75 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: o.key === value ? 600 : 500, flex: 1, pr: 2 }}>
              {o.label}
            </Typography>
            <CountBadge count={counts?.[o.key]} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/**
 * The track a segmented control and its neighbouring actions sit in.
 *
 * Shared so the two cannot drift: an action beside the control has to be the same height
 * to the pixel, and matching by eye means re-matching every time either one's padding
 * changes.
 */
const trackSx = {
  p: '2px',
  borderRadius: '8px',
  backgroundColor: (t) => softFill(t),
  border: (t) => `1px solid ${hairline(t)}`,
  display: 'flex',
};

/**
 * A cell lifted off the track — the selected segment, and an action's hover state.
 *
 * `background.paper` is within a couple of points of the track on the dark theme, so the
 * indicator disappeared. Lift it off the track instead of relying on the surface token.
 */
const raisedSx = (t) => ({
  borderRadius: '6px',
  backgroundColor: t.palette.mode === 'dark' ? 'rgba(255,255,255,0.17)' : '#fff',
  border: `1px solid ${t.palette.mode === 'dark' ? 'rgba(255,255,255,0.13)' : 'rgba(0,0,0,0.07)'}`,
  boxShadow: t.palette.mode === 'dark' ? '0 1px 2px rgba(0,0,0,0.45)' : '0 1px 3px rgba(0,0,0,0.16)',
});

/** The type and padding of one segment, so an action reads as one of them. */
const segmentSx = { py: '5px', px: 1.25, fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' };

/**
 * An action that sits beside a `SegmentedControl` and matches it.
 *
 * Was an outlined MUI `Button`, which put a heavy bordered pill next to a soft track —
 * two different weights of control on one line, and the button read as the louder of the
 * two despite being the less common thing to want.
 *
 * Drawn as a track holding one cell, rather than as a single styled element, so its height
 * comes out identical to the control's by construction: same track padding, same border,
 * same cell padding and type.
 *
 * The cell is flat until hover, when it takes the indicator's raised look. A permanently
 * raised cell would read as a selected segment — as though this were another view of the
 * list rather than something that happens when you click it.
 */
export const SegmentedAction = ({ children, onClick, disabled, sx }) => (
  <Box sx={{ ...trackSx, flexShrink: 0, ...sx }}>
    <ButtonBase
      disabled={disabled}
      onClick={onClick}
      sx={{
        ...segmentSx,
        borderRadius: '6px',
        color: 'text.secondary',
        // The border is transparent rather than absent so the cell does not grow by 2px
        // on hover and nudge the whole row.
        border: '1px solid transparent',
        transition: 'background-color 180ms ease, color 180ms ease',
        '&:hover': (t) => ({ ...raisedSx(t), color: 'text.primary' }),
        '&.Mui-disabled': { color: 'text.disabled' },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      {children}
    </ButtonBase>
  </Box>
);

export const SegmentedControl = ({ value, options, onChange, sx }) => {
  const idx = Math.max(
    options.findIndex((o) => o.key === value),
    0,
  );
  const share = 100 / options.length;
  return (
    <Box sx={{ ...trackSx, ...sx }}>
      {/* Grid, not flex. `flex: 0 0 <share>%` resolves the basis against the
          container and ignores each child's min-content, so the longest label
          overflowed its own padding.

          `minmax(0, 1fr)`, not a bare `1fr`. `1fr` means `minmax(auto, 1fr)`, whose auto
          minimum floors each column at its own min-content — so the columns stop being
          equal exactly when space runs short. At 320px these three resolved to
          21.9/43.6/48.0px inside an 86.8px track: the columns overflowed the track, and
          the indicator below — which is a fixed `share%` translated by whole multiples of
          itself — sat up to 19px away from the option it was marking. A 0 minimum makes
          the columns exactly 1/N at every width, which is the assumption the indicator's
          arithmetic has always made.

          The floor is not needed to stop labels overflowing any more: below sm they wrap
          (see the segment styles), and at sm and up this track is auto-width, where 1fr
          columns still resolve to the widest label's max-content. */}
      <Box
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={(t) => ({
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: `${share}%`,
            transform: `translateX(${idx * 100}%)`,
            ...raisedSx(t),
            transition: 'transform 260ms cubic-bezier(0.32, 0.72, 0, 1)',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          })}
        />
        {options.map((o) => (
          <Box
            key={o.key}
            onClick={() => onChange(o.key)}
            sx={{
              ...segmentSx,
              // The control's segments are tighter than a standalone action's: three of
              // them share a row, and the action has to hold its own label alone.
              // Tighter still on a phone: the grid floors each column at its label's
              // intrinsic width, so three nowrap labels plus 8px of padding each came to
              // ~290px against the ~270px a card actually has, and the last segment was
              // cut off. The padding is the only part of that which is discretionary.
              px: { xs: 0.5, sm: 1 },
              fontSize: { xs: '11.5px', sm: '12px' },
              // And below sm the labels may take a second line. Even at the tighter
              // padding, three nowrap labels floor the grid at ~250px, which is wider
              // than a card on a 320px screen — so "All actions" was cut off. Wrapping
              // drops each column's floor to its longest WORD and keeps all three
              // options visible, which hiding or scrolling one of them would not.
              whiteSpace: { xs: 'normal', sm: 'nowrap' },
              lineHeight: 1.2,
              position: 'relative',
              zIndex: 1,
              textAlign: 'center',
              cursor: 'pointer',
              color: value === o.key ? 'text.primary' : 'text.secondary',
              transition: 'color 180ms ease',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          >
            {o.label}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Real zoom / rotate / pan, via the same ImagePreview the current approvals page
// uses. `key` forces a fresh viewer when the row changes: ImagePreview builds its
// Viewer once on mount, so without a remount the previous document's canvas is
// reused and keeps showing the old scan.
export const DocViewer = ({ id, src }) => (
  <Box sx={{ height: '100%', minHeight: 0, borderRadius: '8px', overflow: 'hidden' }}>
    <ImagePreview key={id} id={`doc-${id}`} src={src} height="100%" />
  </Box>
);

export const EmptyViewer = ({ children }) => (
  <Box
    sx={{
      height: '100%',
      minHeight: 240,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      px: 3,
      border: (t) => `1px dashed ${hairline(t)}`,
      borderRadius: '8px',
    }}
  >
    <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{children}</Typography>
  </Box>
);

/**
 * Whose turn it is, on the row.
 *
 * Ours is accent and bold because it is the only kind of row a reviewer can clear;
 * the driver's is muted because reading it is the entire action. This replaced a
 * "not blocking" whisper in the same slot, which said something the row's own
 * sentence already spells out — and never answered the question people actually
 * scanned this list with.
 */
export const WaitingTag = ({ waitingOn }) => {
  // Only `us` is a queue. `system` is a PENDING document nothing has looked at, and
  // it says "not verified" rather than anything with "review" in it — that word is
  // reserved for the one state where a person is the named next step.
  const label = { us: 'Needs review', driver: 'On the driver', system: 'Not verified' }[waitingOn];
  if (!label) return null;
  const ours = waitingOn === 'us';
  return (
    <Typography
      sx={{
        fontSize: '11px',
        fontWeight: ours ? 700 : 500,
        color: ours ? 'accent.main' : 'text.disabled',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  );
};

export const RemedyLine = ({ remedy }) => {
  if (!remedy) return null;
  return (
    <Typography sx={{ fontSize: '12px', color: 'accent.main', fontWeight: 600 }}>{REMEDIES[remedy].label} →</Typography>
  );
};

/**
 * What the confirmation dialog says, per state.
 *
 * Approving is confirmed because the latch is one-way: it is the moment a person stops
 * being an applicant and their status starts being derived from documents instead of
 * chosen. Nothing undoes that.
 */
const confirmCopy = (state, unresolved, reasonEnabled = true) => {
  const n = `${unresolved} requirement${unresolved === 1 ? '' : 's'}`;
  if (state.needsReason) {
    return reasonEnabled
      ? 'Deactivation is terminal — the only way back is Cleared. The reason is recorded on the standing event.'
      : 'Deactivation is terminal — the only way back is Cleared.';
  }
  if (state.key === 'approved') {
    return `Approving is one-way. From here on this driver is governed by standing — derived from their documents — and the funnel statuses no longer apply.${
      unresolved ? ` ${n} are still unresolved.` : ''
    }`;
  }
  return `${n} are still unresolved. Setting this anyway is allowed and will be recorded as your decision, but standing may re-derive once documents change.`;
};

/**
 * The header pill IS the status control — no separate standing section.
 *
 * Pill and menu name the SAME thing: states, in the vocabulary of the driver's phase.
 * A menu of verbs ("Approve", "Send to review") next to a pill reading "In review" gave
 * a reviewer nothing to match their driver's current status against, which is the
 * confusion this replaced.
 *
 * The phase goes before the status because one pill serves two vocabularies, and words
 * appear in both: `awaiting_docs` pre-approval is someone still applying, and
 * post-approval is a working driver whose insurance lapsed, because standing mirrors
 * down onto the funnel. Without the phase the pill is ambiguous exactly where being
 * wrong costs the most.
 *
 * Which phase's list applies is the server's rule, not a presentation choice — it 400s
 * a state from the wrong side. Unsettable states stay in the list, disabled, so the
 * pill's vocabulary is complete: a reviewer should never look for their own driver's
 * status in a menu that does not contain it. See `availableActions` for what disables
 * what.
 *
 * Setting a state with unresolved requirements is *warned about, not blocked*. The
 * server deliberately applies no guard to a human judgement, so blocking it here would
 * make the same decision possible or impossible depending on which surface it came
 * through. Naming what is still unresolved is this component's job.
 */
export const StandingPill = ({
  standing,
  applicationStatus,
  hasBeenApproved,
  unresolved = 0,
  onAction,
  busy,
  allowAll = false,
  reasonEnabled = true,
  onReevaluate,
}) => {
  const [anchor, setAnchor] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [reason, setReason] = useState('');

  const head = phaseStatus({ hasBeenApproved, currentStanding: standing, applicationStatus });
  const states = availableActions(
    { hasBeenApproved, currentStanding: standing, applicationStatus },
    { allowAll },
  );

  // Absent handler, absent section: the user page mounts a second pill for application
  // status, and standing re-evaluation has nothing to say about an applicant.
  const reevaluation = onReevaluate
    ? canReevaluateStanding({ hasBeenApproved, currentStanding: standing })
    : null;

  const start = (state) => {
    setAnchor(null);
    // Deactivation needs a reason, approval is one-way, and clearing over unresolved
    // work deserves a second look; everything else applies straight away.
    if (state.needsReason || state.confirm || (state.key === 'cleared' && unresolved > 0)) {
      setReason('');
      setConfirming(state);
      return;
    }
    onAction(state, null);
  };

  const commit = () => {
    const state = confirming;
    setConfirming(null);
    onAction(state, reason.trim() || null);
  };

  const reasonRequired = reasonEnabled && Boolean(confirming?.needsReason);

  return (
    <>
      <Stack
        component="button"
        type="button"
        direction="row"
        alignItems="center"
        spacing={0.75}
        disabled={busy}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          border: 0,
          background: 'none',
          font: 'inherit',
          p: 0,
          cursor: busy ? 'default' : 'pointer',
          opacity: busy ? 0.5 : 1,
          '&:hover': { opacity: busy ? 0.5 : 0.8 },
          // The phase word, the status pill and the chevron come to ~130px and none of
          // them shrank, so on a 320px screen the whole control — which is the thing you
          // tap to change a driver's status — hung off the edge of the card. Allowed to
          // wrap onto a second line instead.
          flexWrap: 'wrap',
          minWidth: 0,
          maxWidth: '100%',
          rowGap: 0.25,
        }}
      >
        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', fontWeight: 600 }}>{head.phase}</Typography>
        <TintedStatusPill palette={head.palette} label={head.label} />
        <ChevronDownIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      </Stack>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { minWidth: 278, mt: 0.5 } }}
      >
        <Box sx={{ px: 1.75, py: 1, maxWidth: 300 }}>
          <SectionLabel sx={{ display: 'block', mb: 0.25 }}>
            {hasBeenApproved ? 'Driver standing' : 'Application status'}
          </SectionLabel>
          <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>
            {hasBeenApproved
              ? 'Cleared and invalid information roll up from document validity — resolving the requirements below is what changes those.'
              : 'Still an applicant, so the funnel owns their status. Standing takes over once they are approved.'}
          </Typography>
        </Box>
        <Divider sx={{ borderColor: (t) => hairline(t) }} />
        {states.map((s) => (
          <MenuItem
            key={s.key}
            disabled={s.disabled}
            onClick={() => start(s)}
            sx={{ py: 0.75, '&.Mui-disabled': { opacity: 1 } }}
          >
            <Box sx={{ flex: 1, minWidth: 0, opacity: s.disabled ? 0.55 : 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: s.isCurrent ? 700 : 600,
                    color: s.palette === 'error' && !s.disabled ? 'error.main' : 'text.primary',
                  }}
                >
                  {s.label}
                </Typography>
                {/* A tick on the current one, so the pill's value is findable in the
                    list at a glance rather than by reading every row. */}
                {s.isCurrent && <CheckCircleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
              </Stack>
              <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>
                {s.disabled
                  ? s.disabledHint
                  : s.key === 'cleared' && unresolved > 0
                    ? `${unresolved} still unresolved`
                    : s.hint}
              </Typography>
            </Box>
          </MenuItem>
        ))}
        {reevaluation && <Divider sx={{ borderColor: (t) => hairline(t) }} />}
        {reevaluation && (
          <MenuItem
            disabled={!reevaluation.enabled}
            onClick={() => {
              setAnchor(null);
              onReevaluate();
            }}
            sx={{ py: 0.75, '&.Mui-disabled': { opacity: 1 } }}
          >
            {/* Opacity on the inner box rather than the item, matching the state rows
                above: it dims the words while leaving the row's own height and hover
                target where a reviewer's eye already expects them. */}
            <Box sx={{ flex: 1, minWidth: 0, opacity: reevaluation.enabled ? 1 : 0.55 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Re-evaluate standing</Typography>
              <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{reevaluation.hint}</Typography>
            </Box>
          </MenuItem>
        )}
      </Menu>

      <Dialog open={Boolean(confirming)} onClose={() => setConfirming(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>{confirming?.label}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 2 }}>
            {confirming && confirmCopy(confirming, unresolved, reasonEnabled)}
          </Typography>
          {reasonEnabled && (
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={2}
              size="small"
              label={reasonRequired ? 'Reason (required)' : 'Reason (optional)'}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirming?.palette === 'error' ? 'error' : 'primary'}
            disabled={reasonRequired && !reason.trim()}
            onClick={commit}
            sx={{ textTransform: 'none' }}
          >
            {confirming?.label}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * The bordered card every section on the record uses.
 *
 * Extracted so the driver, each vehicle, the background check and the event log
 * are the same object rather than four approximations of it — the tinted header
 * band and the hairline body are what make the grouping visually real instead of
 * implied by a heading.
 *
 * `band` is extra content inside the tinted header, for a card whose subject has
 * facts of its own (a vehicle's plate and VIN). `meta` is the right-aligned
 * summary on the header row.
 */
export const SectionCard = ({ title, icon, meta, band, children, sx }) => (
  <Box sx={{ border: (t) => `1px solid ${hairline(t)}`, borderRadius: '9px', overflow: 'hidden', ...sx }}>
    <Box sx={{ backgroundColor: (t) => softFill(t) }}>
      {/* Wraps, and the title is allowed to shrink. This row holds an icon, a title, a
          `TrailingMeta` spacer, whatever the meta is, and sometimes an action button —
          held on one line, the trailing end ran off a card that clips its overflow, and
          on the Driver balance card that put the "Add a payment" button ~40px past the
          right edge of a phone, where it could not be tapped at all.

          `minWidth: 0` on the title lets it give up space before the action does: a
          truncated section title is legible from context, a missing button is not. */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1.75, py: 1.25, flexWrap: { xs: 'wrap', sm: 'nowrap' }, rowGap: 0.5 }}
      >
        {icon}
        <Typography
          sx={{
            fontSize: '13.5px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>
        {meta}
      </Stack>
      {band}
    </Box>
    {children}
  </Box>
);

/**
 * Where rows would be, when there are none.
 *
 * Row-shaped rather than a paragraph, so an empty section reads as a section with
 * nothing in it rather than as an explanation of why it is missing.
 */
export const EmptyRows = ({ children }) => (
  <Stack alignItems="center" justifyContent="center" sx={{ px: 2, py: 2.5 }}>
    <Typography sx={{ fontSize: '12px', color: 'text.disabled', textAlign: 'center' }}>{children}</Typography>
  </Stack>
);

/**
 * A section the backend cannot fill yet.
 *
 * Rendered rather than omitted, and deliberately not filled with plausible
 * numbers: the data exists in Postgres but has no read endpoint, and a section
 * quietly missing reads as "this driver has none of that" to whoever uses the
 * page next.
 */
export const PendingBackend = ({ title, children }) => (
  <Box sx={{ border: (t) => `1px dashed ${hairline(t)}`, borderRadius: '8px', px: 1.75, py: 1.5 }}>
    <SectionLabel sx={{ display: 'block', mb: 0.5 }}>{title}</SectionLabel>
    <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>{children}</Typography>
  </Box>
);
